import React, { useEffect, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  ScrollView,
  Alert,
} from 'react-native';
import { ms, s, vs } from '../../../../utils/responsive';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { RootStackNavigationProp, RootStackParamList } from '../../../../types/navigation';
import { BottomNavBar } from '../../../../components/BottomNavBar';
import {
  PetCarePreferencesData,
  petCarePreferencesService,
} from '../../../../services/petCarePreferencesInfo';
import { tasksService } from '../../../../services/tasks';

import { createLogger } from '../../../../utils/logger';
const logger = createLogger('PetCarePreferencesData');

type PetCarePreferencesDataScreenRouteProp = RouteProp<RootStackParamList, 'PetCarePreferencesData'>;

type RouteParams = {
  taskId: number;
  targetUserId?: string;
  viewOnly?: boolean;
  petCarePreferencesId?: number;
};

type InfoItemProps = {
  label: string;
  value: string | boolean | undefined;
};

const InfoItem: React.FC<InfoItemProps> = ({ label, value }) => {
  let displayValue = '';

  if (typeof value === 'boolean') {
    displayValue = value ? 'Yes' : 'No';
  } else if (typeof value === 'string') {
    displayValue = value || 'Not specified';
  } else {
    displayValue = 'Not specified';
  }

  return (
    <View style={styles.infoItem}>
      <Text style={styles.label}>{label}</Text>
      <Text style={styles.value}>{displayValue}</Text>
      <View style={styles.separator} />
    </View>
  );
};

export const PetCarePreferencesDataScreen: React.FC = () => {
  const navigation = useNavigation<RootStackNavigationProp>();
  const route = useRoute<PetCarePreferencesDataScreenRouteProp>();
  const [petCarePreferencesInfo, setPetCarePreferencesInfo] = useState<PetCarePreferencesData | null>(null);
  const [loading, setLoading] = useState(true);

  const petCarePreferencesId = route.params?.petCarePreferencesId;
  const targetUserId = route.params?.targetUserId;
  const viewOnly = route.params?.viewOnly;

  useEffect(() => {
    loadPetCarePreferencesInfo();
  }, []);

  const loadPetCarePreferencesInfo = async () => {
    try {
      if (petCarePreferencesId) {
        const info = await petCarePreferencesService.getPetCarePreferencesById(petCarePreferencesId, targetUserId);
        setPetCarePreferencesInfo(info);
      } else {
        // Load the first/latest entry if no specific ID provided
        const allInfo = await petCarePreferencesService.getAllPetCarePreferences(targetUserId);
        if (allInfo.length > 0) {
          setPetCarePreferencesInfo(allInfo[0]);
        }
      }
    } catch (error) {
      logger.error('Error loading pet care preferences info:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    if (petCarePreferencesInfo) {
      // Map the data from petCarePreferencesInfo to the format expected by PetCarePreferencesInputScreen
      const mappedData = {
        id: petCarePreferencesInfo.id,
        caretakerName: petCarePreferencesInfo.caretakerName,
        caretakerRelationship: petCarePreferencesInfo.caretakerRelationship,
        caretakerPhone: petCarePreferencesInfo.caretakerPhone,
        caretakerEmail: petCarePreferencesInfo.caretakerEmail,
        alternateCaretakerName: petCarePreferencesInfo.alternateCaretakerName,
        alternateCaretakerRelationship: petCarePreferencesInfo.alternateCaretakerRelationship,
        alternateCaretakerPhone: petCarePreferencesInfo.alternateCaretakerPhone,
        alternateCaretakerEmail: petCarePreferencesInfo.alternateCaretakerEmail,
        feedingInstructions: petCarePreferencesInfo.feedingInstructions,
        medicationInstructions: petCarePreferencesInfo.medicationInstructions,
        veterinarianName: petCarePreferencesInfo.veterinarianName,
        veterinarianPhone: petCarePreferencesInfo.veterinarianPhone,
        veterinarianAddress: petCarePreferencesInfo.veterinarianAddress,
        petInsuranceInfo: petCarePreferencesInfo.petInsuranceInfo,
        specialInstructions: petCarePreferencesInfo.specialInstructions,
        notes: petCarePreferencesInfo.notes,
      };

      navigation.navigate('PetCarePreferencesInput', {
        petCarePreferencesData: mappedData,
        isEditing: true
      });
    } else {
      navigation.navigate('PetCarePreferencesInput');
    }
  };

  const handleDelete = () => {
    if (petCarePreferencesInfo) {
      Alert.alert(
        'Delete Pet Care Preferences',
        'Are you sure you want to delete these pet care preferences? This action cannot be undone.',
        [
          {
            text: 'Cancel',
            style: 'cancel',
          },
          {
            text: 'Delete',
            style: 'destructive',
            onPress: async () => {
              try {
                await petCarePreferencesService.deletePetCarePreferences(petCarePreferencesInfo.id!);

                // CRITICAL: Check if this was the last entry and reset task if so
                const remainingData = await petCarePreferencesService.getAllPetCarePreferences();
                if (!remainingData || remainingData.length === 0) {
                  await tasksService.resetTask('pet_care_preferences');
                }

                (navigation as any).navigate('CarePreferences', { screen: 'CarePreferencesChecklist', params: { refreshKey: Date.now() } });
              } catch (error) {
                logger.error('Error deleting pet care preferences info:', error);
                Alert.alert('Error', 'Failed to delete pet care preferences');
              }
            },
          },
        ]
      );
    }
  };

  const handleBack = () => {
    (navigation as any).navigate('CarePreferences', { screen: 'CarePreferencesChecklist', params: { refreshKey: Date.now() } });
  };

  if (loading) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.header}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigation.goBack()}
          >
            <Image
              source={require('../../../../assets/icons/back.png')}
              style={styles.backIcon}
            />
          </TouchableOpacity>
          <Image
            source={require('../../../../assets/logo.png')}
            style={styles.logo}
            resizeMode="contain"
          />
          <TouchableOpacity style={styles.notificationButton}>
            <Image
              source={require('../../../../assets/icons/notification.png')}
              style={styles.notificationIcon}
            />
          </TouchableOpacity>
        </View>
        <Text style={styles.title}>Pet Care Preferences Details</Text>
        <View style={styles.whiteContainer}>
          <View style={styles.loadingContainer}>
            <Text style={styles.loadingText}>Loading...</Text>
          </View>
        </View>
        <BottomNavBar />
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Image
            source={require('../../../../assets/icons/back.png')}
            style={styles.backIcon}
          />
        </TouchableOpacity>
        <Image
          source={require('../../../../assets/logo.png')}
          style={styles.logo}
          resizeMode="contain"
        />
        <TouchableOpacity style={styles.notificationButton}>
          <Image
            source={require('../../../../assets/icons/notification.png')}
            style={styles.notificationIcon}
          />
        </TouchableOpacity>
      </View>

      <Text style={styles.title}>Pet Care Preferences Details</Text>
      <View style={styles.whiteContainer}>
        {viewOnly && (
          <View style={styles.viewOnlyBanner}>
            <Text style={styles.viewOnlyText}>View Only - You are viewing another user's data</Text>
          </View>
        )}
        <ScrollView style={styles.scrollView}>
          {petCarePreferencesInfo ? (
            <>
              <InfoItem label="Primary Caretaker Name" value={petCarePreferencesInfo.caretakerName} />
              <InfoItem label="Relationship" value={petCarePreferencesInfo.caretakerRelationship} />
              <InfoItem label="Primary Caretaker Phone" value={petCarePreferencesInfo.caretakerPhone} />
              <InfoItem label="Primary Caretaker Email" value={petCarePreferencesInfo.caretakerEmail} />

              {petCarePreferencesInfo.alternateCaretakerName && (
                <InfoItem label="Alternate Caretaker Name" value={petCarePreferencesInfo.alternateCaretakerName} />
              )}

              {petCarePreferencesInfo.alternateCaretakerRelationship && (
                <InfoItem label="Alternate Relationship" value={petCarePreferencesInfo.alternateCaretakerRelationship} />
              )}

              {petCarePreferencesInfo.alternateCaretakerPhone && (
                <InfoItem label="Alternate Caretaker Phone" value={petCarePreferencesInfo.alternateCaretakerPhone} />
              )}

              {petCarePreferencesInfo.alternateCaretakerEmail && (
                <InfoItem label="Alternate Caretaker Email" value={petCarePreferencesInfo.alternateCaretakerEmail} />
              )}

              {petCarePreferencesInfo.feedingInstructions && (
                <InfoItem label="Feeding Instructions" value={petCarePreferencesInfo.feedingInstructions} />
              )}

              {petCarePreferencesInfo.medicationInstructions && (
                <InfoItem label="Medication Instructions" value={petCarePreferencesInfo.medicationInstructions} />
              )}

              {petCarePreferencesInfo.veterinarianName && (
                <InfoItem label="Veterinarian Name" value={petCarePreferencesInfo.veterinarianName} />
              )}

              {petCarePreferencesInfo.veterinarianPhone && (
                <InfoItem label="Veterinarian Phone" value={petCarePreferencesInfo.veterinarianPhone} />
              )}

              {petCarePreferencesInfo.veterinarianAddress && (
                <InfoItem label="Veterinarian Address" value={petCarePreferencesInfo.veterinarianAddress} />
              )}

              {petCarePreferencesInfo.petInsuranceInfo && (
                <InfoItem label="Pet Insurance Information" value={petCarePreferencesInfo.petInsuranceInfo} />
              )}

              {petCarePreferencesInfo.specialInstructions && (
                <InfoItem label="Special Instructions" value={petCarePreferencesInfo.specialInstructions} />
              )}

              {petCarePreferencesInfo.notes && (
                <InfoItem label="Additional Notes" value={petCarePreferencesInfo.notes} />
              )}

              <View style={styles.buttonContainer}>
                <TouchableOpacity
                  style={[styles.button, styles.backToListButton]}
                  onPress={handleBack}
                >
                  <Text style={styles.buttonText}>Back</Text>
                </TouchableOpacity>

                {!viewOnly && (
                  <TouchableOpacity
                    style={[styles.button, styles.deleteButton]}
                    onPress={handleDelete}
                  >
                    <Text style={styles.buttonText}>Delete</Text>
                  </TouchableOpacity>
                )}

                {!viewOnly && (
                  <TouchableOpacity
                    style={[styles.button, styles.editButton]}
                    onPress={handleEdit}
                  >
                    <Text style={styles.buttonText}>Edit</Text>
                  </TouchableOpacity>
                )}
              </View>
            </>
          ) : (
            <View style={styles.noDataContainer}>
              <Text style={styles.noDataText}>No pet care preferences found.</Text>
              {!viewOnly && (
                <TouchableOpacity
                  style={[styles.button, styles.editButton, { marginTop: 20 }]}
                  onPress={handleEdit}
                >
                  <Text style={styles.buttonText}>Add Pet Care Preferences</Text>
                </TouchableOpacity>
              )}
            </View>
          )}
        </ScrollView>
      </View>

      <BottomNavBar />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#29BFC0', // Teal header background
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: ms(15),
    paddingTop: ms(50),
    position: 'relative',
  },
  backButton: {
    position: 'absolute',
    left: ms(50),
    top: ms(15),
    padding: ms(5),
    zIndex: 1,
  },
  backIcon: {
    width: s(32),
    height: s(32),
    tintColor: '#333',
  },
  logo: {
    width: s(125),
    height: s(125),
  },
  notificationButton: {
    position: 'absolute',
    right: ms(15),
    top: '50%',
    transform: [{ translateY: -12 }],
    padding: ms(5),
  },
  notificationIcon: {
    width: s(24),
    height: s(24),
    tintColor: '#333',
  },
  title: {
    fontSize: ms(24, 0.3),
    fontWeight: '600',
    color: '#FFFFFF', // White title on teal background
    textAlign: 'center',
    marginVertical: ms(20),
  },
  whiteContainer: {
    flex: 1,
    backgroundColor: '#FFFFFF', // White content area
    borderTopLeftRadius: ms(30),
    borderTopRightRadius: ms(30),
    paddingTop: ms(20),
  },
  scrollView: {
    flex: 1,
    paddingHorizontal: ms(20),
  },
  infoItem: {
    marginBottom: ms(20),
  },
  label: {
    fontSize: ms(16, 0.3),
    color: '#6B7280', // Gray label text
    marginBottom: ms(8),
  },
  value: {
    fontSize: ms(18, 0.3),
    color: '#1C2C4F', // Dark blue value text
    marginBottom: ms(8),
  },
  separator: {
    height: s(2),
    backgroundColor: '#29BFC0', // Teal separator line
    marginTop: ms(8),
  },
  buttonContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: ms(20),
    paddingBottom: ms(130),
    gap: ms(10),
  },
  button: {
    flex: 1,
    padding: ms(15),
    borderRadius: ms(25),
    alignItems: 'center',
  },
  backToListButton: {
    backgroundColor: '#29BFC0', // Teal back button
  },
  deleteButton: {
    backgroundColor: '#FF4444', // Red delete button
  },
  editButton: {
    backgroundColor: '#FF9F5A', // Orange edit button
  },
  buttonText: {
    color: '#FFFFFF',
    fontSize: ms(18, 0.3),
    fontWeight: '600',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: ms(40),
  },
  loadingText: {
    fontSize: ms(18, 0.3),
    color: '#6B7280',
  },
  noDataContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: ms(40),
  },
  noDataText: {
    fontSize: ms(18, 0.3),
    color: '#6B7280',
    textAlign: 'center',
  },
  sectionTitle: {
    fontSize: ms(20, 0.3),
    fontWeight: 'bold',
    color: '#1C2C4F',
    marginTop: ms(20),
    marginBottom: ms(15),
  },
  viewOnlyBanner: {
    backgroundColor: '#FF9F5A',
    paddingVertical: ms(8),
    paddingHorizontal: ms(15),
    marginHorizontal: ms(20),
    borderRadius: ms(8),
    marginBottom: ms(10),
  },
  viewOnlyText: {
    color: '#FFFFFF',
    fontSize: ms(14, 0.3),
    fontWeight: '600',
    textAlign: 'center',
  },
});
